<template>
  <div class="annotation-toolbar">
    <div class="toolbar-header">
      <h4>Add Fields</h4>
    </div>

    <div class="field-types">
      <button class="field-type-btn signature-btn" draggable="true" @dragstart="handleDragStart($event, 'signature')"
        @dragend="handleDragEnd" @click="addField('signature')" :disabled="adding">
        <i class="fa fa-signature"></i>
        <span>Signature</span>
      </button>

      <button class="field-type-btn text-btn" draggable="true" @dragstart="handleDragStart($event, 'text')"
        @dragend="handleDragEnd" @click="addField('text')" :disabled="adding">
        <i class="fa fa-font"></i>
        <span>Text</span>
      </button>

      <button class="field-type-btn select-btn" draggable="true" @dragstart="handleDragStart($event, 'select')"
        @dragend="handleDragEnd" @click="addField('select')" :disabled="adding">
        <i class="fa fa-list"></i>
        <span>Select</span>
      </button>

      <button class="field-type-btn date-btn" draggable="true" @dragstart="handleDragStart($event, 'date')"
        @dragend="handleDragEnd" @click="addField('date')" :disabled="adding">
        <i class="fa fa-calendar"></i>
        <span>Date</span>
      </button>
    </div>

    <div class="toolbar-section" v-if="selectedField && selectedField.type !== 'signature'">
      <h5>Assign To</h5>
      <select v-model="assignableToValue" class="assignee-select">
        <option value="">-- Select Role --</option>
        <option value="student">Student</option>
        <option value="guardian">Guardian</option>
        <option value="instructor">Instructor</option>
        <option value="staff">Staff</option>
      </select>
    </div>

    <div class="toolbar-actions" v-if="adding">
      <p class="help-text">
        <i class="fa fa-info-circle"></i>
        Click on the PDF to place the {{ addingType }} field
      </p>
      <button class="btn btn-default btn-sm" @click="cancelAdd">
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

export default {
  name: 'AnnotationToolbar',
  props: {
    selectedField: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      adding: false,
      addingType: '',
      dragging: false
    };
  },
  computed: {
    assignableToValue: {
      get() {
        const list = this.selectedField && this.selectedField.assignableTo;
        return Array.isArray(list) && list.length ? list[0] : '';
      },
      set(value) {
        if (!this.selectedField) return;
        this.selectedField.assignableTo = value ? [value] : [];
      }
    }
  },
  methods: {
    addField(type) {
      this.adding = true;
      this.addingType = type;
      this.$emit('start-add-field', type);
    },

    cancelAdd() {
      this.adding = false;
      this.addingType = '';
      this.$emit('cancel-add-field');
    },

    completeAdd() {
      this.adding = false;
      this.addingType = '';
    },

    handleDragStart(event, fieldType) {
      this.dragging = true;

      // Set drag data for drop handler
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('application/x-field-type', fieldType);

      // Set a simple text fallback for compatibility
      event.dataTransfer.setData('text/plain', fieldType);

      // Emit drag start event for parent components
      this.$emit('drag-start', { type: fieldType });

      // Add visual feedback class to button
      event.target.classList.add('dragging');
    },

    handleDragEnd(event) {
      this.dragging = false;

      // Remove visual feedback class
      event.target.classList.remove('dragging');

      // Emit drag end event
      this.$emit('drag-end');
    }
  }
};
</script>

<style scoped>
/* Blueprint Editor Theme - Toolbar */
.annotation-toolbar {
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
}

.toolbar-header {
  margin-bottom: 16px;
}

.toolbar-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field-types {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 20px;
}

/* Blueprint-styled field type buttons */
.field-type-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 14px 8px;
  border: 2px solid;
  border-radius: 4px;
  background-color: #fff;
  cursor: grab;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  min-height: 72px;
  width: 100%;
  box-sizing: border-box;
}

.field-type-btn:hover:not(:disabled) {
  cursor: grab;
}

.field-type-btn:active:not(:disabled) {
  cursor: grabbing;
}

.field-type-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.field-type-btn.dragging {
  opacity: 0.6;
  cursor: grabbing;
}

.field-type-btn i {
  font-size: 20px;
  margin-bottom: 6px;
}

.field-type-btn span {
  font-size: 12px;
  font-weight: 500;
}

/* DriveScout color palette for buttons */
.signature-btn {
  border-color: #2B83FC;
  color: #2B83FC;
}

.signature-btn:hover:not(:disabled) {
  background-color: rgba(43, 131, 252, 0.08);
}

.text-btn {
  border-color: #64BC5F;
  color: #64BC5F;
}

.text-btn:hover:not(:disabled) {
  background-color: rgba(100, 188, 95, 0.08);
}

.select-btn {
  border-color: #9090ce;
  color: #9090ce;
}

.select-btn:hover:not(:disabled) {
  background-color: rgba(144, 144, 206, 0.08);
}

.date-btn {
  border-color: #e28002;
  color: #e28002;
}

.date-btn:hover:not(:disabled) {
  background-color: rgba(226, 128, 2, 0.08);
}

.toolbar-section {
  margin-bottom: 20px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.toolbar-section h5 {
  margin: 0 0 12px 0;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.assignee-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 14px;
  color: #2d3748;
  background-color: #fff;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.assignee-select:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.toolbar-actions {
  padding: 14px;
  background-color: #f8fafc;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  text-align: center;
}

.help-text {
  margin: 0 0 10px 0;
  font-size: 13px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.help-text i {
  color: #2B83FC;
}
</style>
